import {isObject, isUndefined} from 'lodash';
import {Store} from 'redux';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {isDevelopment, isNetlify, isProduction} from '../../libs/shared/helpers/src/environment/envHelpers';
import {TaskSourcesEnum} from '../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {hasProperty} from '../../libs/shared/helpers/src/typescript/typeHelpers';
import {FrontConfig} from '../../srcCommon/bundler/bundlerDefineConfig';
import {reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {isTeammateNamespace} from '../helpers/models/namespaceHelpers';
import {TeammateModel} from '../models/api/teammateModel';
import {SettingsUrlModel} from '../models/url/settingsUrlModel';
import {AppState} from '../store/appState';
import {postAsync} from './http/http';

globalWindow.shouldLogInteractionTracking = FrontConfig.DUMP_TRACKING_EVENTS_TO_CONSOLE;

// Uncomment to log all events. Alternatively just set the `DUMP_TRACKING_EVENTS_TO_CONSOLE` env variable.
// globalWindow.shouldLogInteractionTracking = true;

/*
 * Types.
 */

declare global {
  interface Window {
    shouldLogInteractionTracking?: boolean;
  }
}

export enum TrackingTypesEnum {
  COMPOSER_INTEGRATION = 'composer_integration',
  ELECTRON = 'electron',
  FEATURE_DISCOVER = 'feature_discover',
  FLAG_REQUESTED = 'flag_requested',
  INTEGRATION_OPEN = 'integration_open',
  INTEGRATION_MOUSE_ENTER = 'integration_mouse_enter',
  INTEGRATION_MOUSE_LEAVE = 'integration_mouse_leave',
  INTEGRATION_CLOSE = 'integration_close',
  INTERFACE_EVENT = 'interface_event',
  NEW_BATCH_METRICS = 'new_batch_metrics', // Used temporarily to measure impact of new UI tracking (see services/interactionTracking).
  ONBOARDING_STEP = 'onboarding_step',
  PLUGIN_API = 'plugin_api',
  UI_SDK_API = 'ui_sdk_api',
  WIDGET_CLICK = 'widget_click',
  CLIENT_COUNTERS_CHECK = 'client_counters_check',
}

export enum InterfaceEventsEnum {
  ADD_NEW_INBOX = 'add_new_inbox',
  ADD_NEW_VIEW = 'add_new_view',
  ADD_QUICK_RULE = 'add_quick_rule',
  ADD_REMOVE_TAG = 'add_remove_tag',
  CELLS_FILTERS_SELECTED = 'p2.inbox_tab.select',
  CHANNEL_MASS_ARCHIVE_EVENT = 'channel_mass_archive_event',
  CLICK_CLONE = 'click_clone',
  CLICK_CONVERSATION_DETAILS = 'click_conversation_details',
  CLICK_EDIT_SHARED_DRAFT = 'click_edit_shared_draft',
  CLICK_GCAL_RSVP_LINK = 'click_gcal_rsvp_link',
  CLICK_REPLY = 'click_reply',
  CLICK_TAG_WORKSPACE = 'open_tag_workspace',
  CLICK_TEAMMATE_P1 = 'click_teammate_p1',
  CLICK_LENS_P1 = 'click_lens',
  CLICK_TOGGLE_TAG_WORKSPACE = 'toggle_tag_workspace',
  COMMENT_ADD_ZOOM = 'comment_add_zoom',
  CONVERSATION_CELL_DRAG_AND_DROP_TO_EML = 'conversation_cell_drag_and_drop_to_eml',
  DESTROY_MESSAGE = 'destroy_message',
  DRAG_AND_DROP_TO_PRIVATE_TAG = 'drag_and_drop_to_private_tag',
  DUPLICATE_CONVERSATION_BANNER_ACTION = 'duplicate_conversation_banner_action',
  EDIT_VIEW = 'edit_view',
  FOLLOW_CONVERSATION = 'follow_conversation',
  IDLE_RELOAD = 'idle_reload',
  INTEGRATION_INFORMATION_BANNER_ACTION = 'integration_information_banner_action',
  MERGE_DUPLICATE_CONVERSATIONS_MANUALLY = 'merge_duplicate_conversations_manually',
  MESSAGE_DRAG_AND_DROP_TO_EML = 'message_drag_and_drop_to_eml',
  MESSAGE_CELL_DRAG_AND_DROP_TO_EML = 'message_cell_drag_and_drop_to_eml',
  MESSAGE_SENT_LATER = 'message_sent_later',
  MOVE_MENU = 'move_menu',
  OPEN_EML_FILE = 'open_eml_file',
  OPEN_HELP = 'open_help',
  OPEN_MESSAGE_SEND_OPTIONS = 'open_message_send_options',
  OPEN_WORKSPACE_MENU_ROW = 'open_workspace_row',
  PIN_TAG_TO_TEAMMATE_WORKSPACES = 'pin_tag_to_teammate_workspaces',
  POPOUT_VIEWER = 'popout_p3',
  POPOUT_MESSAGE = 'popout_message',
  RECIPIENTS_AUTOSUGGEST_CLICK = 'recipients_autosuggest_click',
  SAVE_EML = 'save_eml',
  SELECT_FONT_FAMILY = 'font_family_select',
  SELECT_FONT_SIZE = 'font_size_select',
  SHARE_MENU = 'share_menu',
  SNOOZE_CONVERSATION = 'snooze_conversation',
  SPLIT_CONVERSATION = 'split_conversation',
  SPLIT_CONVERSATION_BANNER_ACTION = 'split_conversation_banner_action',
  SYSTEM_DARK_MODE_CHANGE = 'system.dark_mode.change',
  // Low and medium priority activities in P3.
  TIMELINE_ACTIVITY_ACTION = 'timeline_activity_action',
  WHATS_NEW = 'whats_new',

  // Search.
  SEARCH_SESSION_START = 'search_session_start',
  SEARCH_SESSION_END = 'search_session_end',
  SEARCH_DROPDOWN_CLICK = 'search_dropdown_click',
  SEARCH_DROPDOWN_EXPAND = 'search_dropdown_expand',
  SEARCH_QUERY = 'search_query',
  SEARCH_P2_CONVERSATION_CLICK = 'search_p2_conversation_click',
  SEARCH_CLEAR_QUERY = 'search_clear_query',
  SEARCH_HINT_CLICK = 'search_hint_click',
  SEARCH_HINT_MORE_FILTERS_CLICK = 'search_hint_more_filters_click',
  SEARCH_QUERY_BUILDER_CLICK = 'search_query_builder_click',
  SEARCH_QUERY_BUILDER_RESET_CLICK = 'search_query_builder_reset_click',
  SEARCH_QUERY_BUILDER_SEARCH_CLICK = 'search_query_builder_search_click',
  SEARCH_NO_RESULTS_HELPFUL_TIP_CLICK = 'search_no_result_helpful_tip_click',
  SEARCH_NO_RESULTS_NUDGE_CLICK = 'search_no_results_nudge_click',

  // In-app demo scheduling.
  DEMO_SCHEDULING_SUCCESS = 'demo_scheduling_success',
  DEMO_SCHEDULING_FAILURE = 'demo_scheduling_failure',
  DEMO_SCHEDULING_SHOWN = 'demo_scheduling_shown',
  DEMO_SCHEDULING_BANNER_SHOWN = 'demo_scheduling_banner_shown',
  DEMO_SCHEDULING_CARD_CLOSED = 'demo_scheduling_card_closed',

  // In-app webinar marketing.
  WEBINAR_CARD_SHOWN = 'webinar_card_shown',

  // Invite teammate popover.
  INVITE_TEAMMATE_POPOVER_SHOWN = 'invite_teammate_popover_shown',
  INVITE_TEAMMATE_POPOVER_SUCCESS = 'invite_teammate_popover_success',
  INVITE_TEAMMATE_POPOVER_HTTP_ERROR = 'invite_teammate_popover_http_error',

  // Message composer.
  COMPOSER_CHANNEL_CHANGE = 'conversation.composer.switch_sender',
  COMPOSER_BANNER_VIEW = 'conversation.composer.banner_view',
  COMPOSER_BANNER_CLICK = 'conversation.composer.banner_click',
  COMPOSER_INSERT = 'conversation.composer.insert',
  COMPOSER_TOOLTIP_VIEW = 'conversation.composer.tooltip_view',
  COMPOSER_TOOLTIP_CLICK = 'conversation.composer.tooltip_click',
  COMPOSER_MODAL_VIEW = 'conversation.composer.modal_view',
  COMPOSER_MODAL_CLICK = 'conversation.composer.modal_click',

  // Comment composer.
  COMMENT_COMPOSER_EXPAND_ICON_CLICK = 'comment_composer.expand_icon.click',
  COMMENT_COMPOSER_MENTION_ICON_CLICK = 'comment_composer.mention_icon.click',
  COMMENT_COMPOSER_EMOJI_ICON_CLICK = 'comment_composer.emoji_icon.click',
  COMMENT_COMPOSER_PLUS_ICON_CLICK = 'comment_composer.plus_icon.click',
  COMMENT_COMPOSER_ZOOM_ICON_CLICK = 'comment_composer.zoom_icon.click',
  COMMENT_COMPOSER_ADD_FILES_ICON_CLICK = 'comment_composer.add_files_icon.click',
  COMMENT_COMPOSER_FORMATTING_ADD = 'comment_composer.formatting.add',
  COMMENT_COMPOSER_SEND_ICON_CLICK = 'comment_composer.send_icon.click',
  COMMENT_COMPOSER_GIF_ICON_CLICK = 'comment_composer.gif_icon.click',
  COMMENT_COMPOSER_GIF_ICON_SEND = 'comment_composer.gif_icon.send',

  // P3 Recent communications / conversations banner
  P3_RECENT_CONVERSATIONS_BANNER_VIEW = 'p3.recent_conversations_banner.view',
  P3_RECENT_CONVERSATIONS_BANNER_CLICK = 'p3.recent_conversations_banner.click',

  // P3 true select all.
  P3_MULTI_SELECT_MODAL_PERFORM_ACTION = 'p3.multi_select_modal.perform_action',
  P3_MULTI_SELECT_MODAL_CLICK = 'p3.multi_select_modal.click',
  P3_MULTI_SELECT_MODAL_WARNING_DISPLAYED = 'p3.multi_select_modal.warning_displayed',

  // P3 toolbar.
  P3_ADD_TAG_USING_DROPDOWN = 'p3.action_dropdown.tag',
  P3_REMOVE_TAG_USING_HEADER = 'p3.conversation_tag.remove',
  P3_ASSIGN_USING_DROPDOWN = 'p3.action_dropdown.assign',

  // P4 contact details.
  P4_CONTACT_DETAILS_VIEW = 'p4.contact_details.view',
  P4_CONTACT_DETAILS_EDIT = 'p4.contact_details.edit',
  P4_CONTACT_DETAILS_OPEN_CONVERSATION = 'p4.contact_details.open_conversation',
  P4_CONTACT_DETAILS_ADD_GROUP = 'p4.contact_details.add_group',
  P4_CONTACT_DETAILS_SWITCH_CONTACT = 'p4.contact_details.switch_contact',
  P4_CONTACT_DETAILS_EDIT_NOTE = 'p4.contact_details.edit_note',
  P4_CONTACT_DETAILS_EDIT_CONTACT_GROUP = 'p4.contact_details.edit_contact_group',
  P4_CONTACT_DETAILS_CLICK = 'p4.contact_details.click',
  P4_CONTACT_DETAILS_ADD_TO_CONTACTS = 'p4.contact_details.add_to_contacts',
  P4_CONTACT_DETAILS_MERGE = 'p4.contact_details.merge',
  P4_CONTACT_DETAILS_ERROR_DISPLAYED = 'p4.contact_details.error_displayed',
  P4_CONTACT_DETAILS_HOVER = 'p4.contact_details.hover',
  P4_CONTACT_DETAILS_SWITCH_TABS = 'p4.contact_details.switch_tabs',
  P4_CONTACT_DETAILS_EDIT_ACCOUNT = 'p4.contact_details.edit_account',
  P4_CONTACT_DETAILS_CONVERT_CONTACT_TYPE = 'p4.contact_details.convert_contact_type',

  // Contact card.
  CONTACT_CARD_VIEW = 'contact_card.details.view',
  CONTACT_CARD_NOTES_CLICK = 'contact_card.notes_link.click',
  CONTACT_CARD_CONVERSATIONS_CLICK = 'contact_card.recent_conversations.click',

  // Calendar.
  CHANGE_CALENDAR_FOCUSED_DAY = 'change_calenadr_focused_day',
  CLICK_CALENDAR_EVENT = 'click_calendar_event',
  CREATE_CALENDAR_EVENT = 'create_calendar_event',
  DELETE_CALENDAR_EVENT = 'delete_calendar_event',
  MARK_RESPONSE_STATUS = 'mark_response_status',
  TOGGLE_CALENDAR = 'toggle_calendar',
  OPEN_CALENDAR_LIST = 'open_calendar_list',
  RESPOND_CALENDAR_INVITE = 'respond_calendar_invite',
  UPDATE_CALENDAR_EVENT = 'update_calendar_event',
  VIEW_CALENDAR_GRID = 'view_calendar_grid',
  VIEW_CALENDAR_PREVIEW = 'view_calendar_preview',
  POPOUT_CALENDAR = 'popout_calendar',
  ADD_CONFERENCE = 'add_conference',
  CLICK_EMAIL_CALENDAR_PARTICIPANTS = 'click_email_calendar_participants',
  CLICK_CALENDAR_NOTIFICATION = 'click_calendar_notification',
  CLICK_PREVIEW_EVENT = 'click_preview_event',
  SEARCH_FOR_CALENDAR = 'search_for_calendar',
  ADD_CALENDAR_TO_LIST = 'add_calendar_to_list',
  OPEN_CALENDAR_EVENT = 'open_calendar_event',
  CALENDAR_EVENT_MODAL_CLICK = 'calendar_event.modal.click',

  // New user onboarding.
  INFO_TIP_VIEW = 'feature_tip.screen.view',
  HELP_CENTER_VIEW = 'p4.getting_started.view',
  HELP_CENTER_TASK_CLICK = 'p4.getting_started.click',
  HELP_CENTER_EXPAND = 'p4.getting_started_icon.expand',
  HELP_CENTER_TASKS_COMPLETED = 'p4.getting_started.completed',
  HELP_CENTER_P1_REMINDER_VIEW = 'p1.reminder_banner.view',
  HELP_CENTER_P1_REMINDER_CLICK = 'p1.reminder_banner.click',
  HELP_CENTER_P4_REMINDER_VIEW = 'p4.reminder_banner.view',
  HELP_CENTER_P4_REMINDER_CLICK = 'p4.reminder_banner.click',
  HELP_CENTER_P4_SEARCH = 'p4.help_center.search',
  HELP_CENTER_P4_SEARCH_CLICK = 'p4.help_center.click',

  // Conversation Summary + Pinned Comments
  CONVERSATION_SUMMARY_SUMMARY_VIEW = 'conversation_summary.summary.view',
  CONVERSATION_SUMMARY_SECTION_VIEW = 'conversation_summary.section.view',
  CONVERSATION_SUMMARY_COMMENT_PIN_ICON_CLICK = 'conversation_summary.comment_pin_icon.click',
  CONVERSATION_SUMMARY_PINNED_COMMENT_CLICK = 'conversation_summary.pinned_comment.click',
  P3_COMMENT_PIN_ICON_CLICK = 'p3.comment_pin_icon.click',

  // Conversation Preview
  P3_CONVERSATION_PREVIEW_CLICK = 'p3.conversation_preview.click',
  P3_CONVERSATION_PREVIEW_VIEW = 'p3.conversation_preview.view',

  // Scheduling links.
  SCHEDULING_P4_VIEW = 'p4.scheduling.view',
  SCHEDULING_P4_COPY_URL = 'p4.scheduling.copy_url',
  SCHEDULING_P4_CREATE = 'p4.scheduling.create',
  SCHEDULING_P4_UPDATE = 'p4.scheduling.update',
  SCHEDULING_P4_DELETE = 'p4.scheduling.delete',
  SCHEDULING_COMPOSER_VIEW = 'composer.scheduling.view',
  SCHEDULING_COMPOSER_COPY_URL = 'composer.scheduling.copy_url',
  SCHEDULING_COMPOSER_CREATE = 'composer.scheduling.create',
  SCHEDULING_COMPOSER_UPDATE = 'composer.scheduling.update',
  SCHEDULING_COMPOSER_DELETE = 'composer.scheduling.delete',
  SCHEDULING_COMPOSER_INSERT = 'composer.scheduling.insert',

  // Canned answers.
  CANNED_ANSWER_CREATE = 'composer.canned_answer.create',
  SETTINGS_CANNED_ANSWER_CREATE = 'settings.canned_answer.create',
  SETTINGS_CANNED_ANSWER_UPDATE = 'settings.canned_answer.update',
  SETTINGS_CANNED_ANSWER_DELETE = 'settings.canned_answer.delete',
  SETTINGS_CANNED_ANSWER_MOVE = 'settings.canned_answer.move',
  CANNED_ANSWER_INSERT = 'composer.canned_answer.insert',

  CANNED_ANSWER_FOLDER_CREATE = 'settings.canned_answer_folder.create',
  CANNED_ANSWER_FOLDER_UPDATE = 'settings.canned_answer_folder.update',
  CANNED_ANSWER_FOLDER_DELETE = 'settings.canned_answer_folder.delete',
  CANNED_ANSWER_FOLDER_MOVE = 'settings.canned_answer_folder.move',

  // Analytics
  ANALYTICS_DOCUMENTATION_CLICK = 'analytics.documentation.click',
  ANALYTICS_HELP_CLICK = 'analytics.help.click',
  ANALYTICS_NAMESPACE_SELECTED = 'analytics.namespace.selected',

  ANALYTICS_REPORT_VIEW = 'analytics.report.view',
  ANALYTICS_VIEW_SCHEDULE = 'analytics.view.schedule',
  ANALYTICS_VIEW_EXPORT = 'analytics.view.export',
  ANALYTICS_VIEW_EXPORT_CSAT = 'analytics.view.export_csat',
  ANALYTICS_VIEW_EXPORT_METRIC_DETAILS = 'analytics.view.export_metric_details',
  ANALYTICS_VIEW_EXPORT_TABLE = 'analytics.view.export_table',
  ANALYTICS_VIEW_EXPORT_CHART = 'analytics.view.export_chart',

  ANALYTICS_VIEW_VIEW = 'analytics.view.view',
  ANALYTICS_ADD_VIEW_CLICK = 'analytics.add_view.click',
  ANALYTICS_SAVE_VIEW_CLICK = 'analytics.save_view.click',
  ANALYTICS_VIEW_CREATE = 'analytics.view.create',
  ANALYTICS_EDIT_VIEW_CLICK = 'analytics.edit_view.click',
  ANALYTICS_VIEW_EDIT = 'analytics.view.edit',
  ANALYTICS_VIEW_CLOSE = 'analytics.view.close',

  ANALYTICS_ADD_FILTER_CLICK = 'analytics.add_filter.click',
  ANALYTICS_FILTER_SELECT = 'analytics.filter.select',
  ANALYTICS_FILTER_EDIT = 'analytics.filter.edit',
  ANALYTICS_FILTER_REMOVE = 'analytics.filter.remove',
  ANALYTICS_RESET_FILTER_CLICK = 'analytics.reset_filter.click',
  ANALYTICS_DATE_RANGE_CLICK = 'analytics.date_range.click',
  ANALYTICS_DATE_RANGE_SELECT = 'analytics.date_range.select',
  ANALYTICS_METRIC_SELECT = 'analytics.metric.select',

  ANALYTICS_METRIC_LIST_CLICK = 'analytics.metric_list.click',
  ANALYTICS_METRIC_LIST_EDIT = 'analytics.metric_list.edit',
  ANALYTICS_COLUMN_LIST_EDIT = 'analytics.column_list.edit',
  ANALYTICS_COMPARE_PREVIOUS_CLICK = 'analytics.compare_previous.click',
  ANALYTICS_DETAILS_DRAWER_OPEN = 'analytics.details_drawer.open',
  ANALYTICS_DETAILS_DRAWER_CLICK = 'analytics.details_drawer.click',

  ANALYTICS_VIEW_OPEN = 'analytics.view.open',
  ANALYTICS_ACTION_MENU_CLICK = 'analytics.action_menu.click',
  ANALYTICS_DUPLICATE_VIEW_CLICK = 'analytics.duplicate_view.click',
  ANALYTICS_TABLE_FILTER = 'analytics.table.filter',
  ANALYTICS_FILTER_CLICK = 'analytics.filter.click',
  ANALYTICS_SORT_VALUES_CLICK = 'analytics.sort_values.click',

  // Help widget events.
  HELP_WIDGET_HELP_CENTER_CLICK = 'settings_help_widget_help_center_click',
  HELP_WIDGET_SUPPORT_CLICK = 'settings_help_widget_support_click',
  HELP_WIDGET_RESOURCES_CLICK = 'settings_help_widget_resources_click',
  HELP_WIDGET_OPEN_CLICK = 'settings_help_widget_open_click',

  SETTINGS_SEARCH_QUERY = 'settings.search_bar.query',
  SETTINGS_SEARCH_CLICK = 'settings.search_bar.result_click',

  SETTINGS_PAGE_VIEW = 'settings.page.view',

  SETTINGS_TAG_UPDATE = 'settings.tag.update',
  SETTINGS_TAG_CREATE = 'settings.tag.create',
  SETTINGS_TAG_DELETE = 'settings.tag.delete',
  TAG_EDIT_TAG_DELETE = 'tag_edit.tag.delete',
  SETTINGS_TAG_CLICK = 'settings.tag.click',
  SETTINGS_TAG_ARCHIVE = 'settings.tag.archive',
  TAG_EDIT_TAG_ARCHIVE = 'tag_edit.tag.archive',
  SETTINGS_TAG_NEST = 'settings.tag.nest',
  SETTINGS_TAG_SEARCH = 'settings.tag.search',
  SETTINGS_TAG_RESTORE = 'settings.tag.restore',
  SETTINGS_TAG_SORT_BY = 'settings.tag.sort_by',

  SETTINGS_RULE_ADD = 'settings.rule.add',
  SETTINGS_RULE_CREATE = 'settings.rule.create',
  SETTINGS_RULE_UPDATE = 'settings.rule.update',
  SETTINGS_RULE_DELETE = 'settings.rule.delete',
  SETTINGS_RULE_VIEW = 'settings.rule.view',

  SETTINGS_DYNAMIC_VARIABLE_CREATE_CLICK = 'settings.dynamic_variable.create_click',
  SETTINGS_DYNAMIC_VARIABLE_LEARN_MORE_CLICK = 'settings.dynamic_variable.learn_more_click',
  SETTINGS_DYNAMIC_VARIABLE_CREATE = 'settings.dynamic_variable.create',
  SETTINGS_DYNAMIC_VARIABLE_EDIT = 'settings.dynamic_variable.edit',

  // P0.
  NAVIGATION_CLICK = 'app.navigation.click',
  USER_PROFILE_CLICK = 'app.user_profile_avatar.click',
  P0_CONTACTS_MANAGER_SEARCH = 'p0.contacts_manager.search',

  // P1 reorder
  P1_WORKSPACE_REORDER = 'p1.workspace_display.reorder',
  P1_TAG_UPDATE_NEST = 'p1.tag.update_nest',
  P1_WORKSPACE_CHEVRON_CLICK = 'p1.workspace_display.chevron_click',
  P1_TAG_CHEVRON_CLICK = 'p1.tag.chevron_click',
  SETTINGS_WORKSPACE_DISPLAY_REORDER = 'settings.workspace_display.reorder',

  // P1 edit sidebar mode
  P1_EDIT_REORDER = 'p1.edit_sidebar.reorder',
  P1_EDIT_NEST = 'p1.edit_sidebar.nest',
  P1_EDIT_UPDATE = 'p1.edit_sidebar.update',
  P1_EDIT_SELECT = 'p1.edit_sidebar.select',
  P1_EDIT_VIEW = 'p1.edit_sidebar.view',
  P1_WORKSPACE_CLICK = 'p1.workspace_display.click',
  P1_WORKSPACE_VIEW = 'p1.workspace_display.view',
  P1_EDIT_CANCEL = 'p1.edit_sidebar.cancel',
  P1_EDIT_HOVER = 'p1.edit_sidebar.hover',

  // P2 tracking
  P2_CELL_CLICK = 'p2.conversation_cell.click',
  P2_CELL_DRAG = 'p2.conversation_cell.drag',

  // Contacts Manager
  CONTACTS_MANAGER_CONTACTS_SECTION_VIEW = 'contacts_manager.contacts_section.view',
  CONTACTS_MANAGER_CARD_GROUPS_SECTION_VIEW = 'contacts_manager.lists_section.view',
  CONTACTS_MANAGER_CONTACT_DETAILS_VIEW = 'contacts_manager.contact_details.view',
  CONTACTS_MANAGER_CONTACT_EDIT = 'contacts_manager.contact.edit',
  CONTACTS_MANAGER_CONTACT_CREATE = 'contacts_manager.contact.create',
  CONTACTS_MANAGER_CONTACT_DELETE = 'contacts_manager.contact.delete',
  CONTACTS_MANAGER_CONTACT_MERGE = 'contacts_manager.contact.merge',
  CONTACTS_MANAGER_CONTACT_MESSAGE = 'contacts_manager.contact.message',
  CONTACTS_MANAGER_CONTACT_CARD_GROUP_EDIT = 'contacts_manager.contact.list_edit',
  CONTACTS_MANAGER_CONTACT_IMPORT = 'contacts_manager.contact.import',
  CONTACTS_MANAGER_CARD_GROUP_CREATE = 'contacts_manager.lists.create',
  CONTACTS_MANAGER_CARD_GROUP_DELETE = 'contacts_manager.lists.delete',
  CONTACTS_MANAGER_CARD_GROUP_UPDATE = 'contacts_manager.lists.update',
  CONTACTS_MANAGER_INFO_BOX_CLICK = 'contacts_manager.info_box.click',

  // Contacts Manager - Accounts Manager
  CONTACTS_MANAGER_ACCOUNTS_VIEW = 'accounts_manager.accounts_list.view',
  CONTACTS_MANAGER_ACCOUNT_VIEW = 'accounts_manager.account.view',
  CONTACTS_MANAGER_ACCOUNT_CREATE = 'accounts_manager.account.create',
  CONTACTS_MANAGER_ACCOUNT_EDIT = 'accounts_manager.account.edit',
  CONTACTS_MANAGER_ACCOUNT_DELETE = 'accounts_manager.account.delete',
  CONTACTS_MANAGER_ACCOUNT_COPY_LINK = 'accounts_manager.account.copy_link',
  CONTACTS_MANAGER_ACCOUNT_IMPORT_SUCCESS = 'accounts_manager.account.import',
  CONTACTS_MANAGER_ACCOUNT_IMPORT_ERROR = 'accounts_manager.import.error',

  // Comment on message
  P3_MESSAGE_TEXT_HIGHLIGHT = 'p3.message_text.highlight',
  P3_MESSAGE_POPOVER_CLICK = 'p3.message_popover.click',
  P3_MESSAGE_COMMENT_CLICK = 'p3.message_comment.click',
  P3_MESSAGE_COMMENT_PREVIEW = 'p3.message_comment.preview',
  P3_MESSAGE_TEXT_COMMENT = 'p3.message_text.comment',

  // Link Tracking
  P3_MESSAGE_LINK_CLICK = 'p3.message_link.click',
  P3_COMMENT_LINK_CLICK = 'p3.comment_link.click',

  // Events for adding the survey template variable.
  SETTINGS_CANNED_ANSWER_ADD_SURVEY = 'settings.canned_answer.add_survey',
  SETTINGS_SIGNATURE_ADD_SURVEY = 'settings.signature.add_survey',

  /** When a user views the variable dropdown when editing a signature */
  SETTINGS_SIGNATURE_VARIABLE_MENU_VIEW = 'settings.signature.variable_menu_view',
  /** When a user selects a value from the variable dropdown when editing a signature */
  SETTINGS_SIGNATURE_VARIABLE_MENU_SELECT = 'settings.signature.variable_menu_select',
  /** When a user views the variable dropdown when editing a canned answer */
  SETTINGS_CANNED_ANSWER_VARIABLE_MENU_VIEW = 'settings.canned_answer.variable_menu_view',
  /** When a user selects a value from the variable dropdown when editing a canned answer */
  SETTINGS_CANNED_ANSWER_VARIABLE_MENU_SELECT = 'settings.canned_answer.variable_menu_select',

  SETTINGS_INVITE_TEAMMATE_CLICK = 'settings.invite_teammate.click',
  SETTINGS_HOMEPAGE_CLICK = 'settings.homepage.click',
  COMPOSER_COPIED_MESSAGE_INSERT = 'composer.copied_message.insert',
  COMMENT_COMPOSER_COMMENT_REACT = 'comment_composer.comment.react',

  // Rule Table Events
  SETTINGS_RULE_TABLE_CREATE_CLICK = 'settings.rule_table.create_click',
  SETTINGS_RULE_TABLE_OPEN_TABLE = 'settings.rule_table.open_table',
  SETTINGS_RULE_TABLE_EXPORT = 'settings.rule_table.export',
  SETTINGS_RULE_TABLE_IMPORT = 'settings.rule_table.import',
  SETTINGS_RULE_TABLE_IMPORT_STARTED = 'settings.rule_table.import_started',
  SETTINGS_RULE_TABLE_IMPORT_COMPLETED = 'settings.rule_table.import_completed',
  SETTINGS_RULE_TABLE_FIRST_NEW_ROW = 'settings.rule_table.first_new_row',
  SETTINGS_RULE_TABLE_LEARN_MORE_CLICK = 'settings.rule_table.learn_more_click',
  SETTINGS_RULE_TABLE_ADD_COLUMN = 'settings.rule_table.add_column',
  SETTINGS_RULE_TABLE_ADD_ROW = 'settings.rule_table.add_row',
  SETTINGS_RULE_TABLE_CREATED = 'settings.rule_table.table_created',
  SETTINGS_RULE_TABLE_EDITED = 'settings.rule_table.table_edited',

  /*
   * Inbox graph events.
   */

  INBOX_OVERVIEW_GRAPH_EXPAND = 'settings.inbox_rules.graph.expand',
  INBOX_OVERVIEW_GRAPH_CLICK = 'settings.inbox_rules.graph.click',

  /*
   * New inbox flow events.
   */
  /** When the user selects a rule type during inbox creation */
  RULE_TYPE_CHOSEN = 'settings_rule_type_chosen',
  /** When the user creates a new rule */
  RULE_CREATED = 'settings_rule_created',
  /** When the user completes the rule selection form */
  RULE_SETUP_COMPLETED = 'settings_rule_setup_completed',

  /** When the user completes the channel selection form */
  CHANNEL_SETUP_COMPLETED = 'settings_channel_setup_completed',

  /** When the user completes the inbox settings form */
  INBOX_SETTINGS_FORM_COMPLETED = 'settings_inbox_settings_form_completed',

  /*
   * Events tracked after channel has been created. The forward settings page for
   * SMTP channels falls under this category, since it's only shown once the
   * channel has actually been created.
   */

  // Events specific to SMTP email channel validation.
  /** When the user first clicks Send test email. */
  SMTP_VALIDATION_STARTED = 'settings_smtp_validation_started',
  /** When the test email is sent. */
  SMTP_VALIDATION_EMAIL_SENT = 'settings_smtp_validation_email_sent',
  /** When the test email takes too long and times out. */
  SMTP_VALIDATION_TIMED_OUT = 'settings_smtp_validation_timed_out',
  /** When the test email is successfully sent. */
  SMTP_VALIDATION_SUCCEEDED = 'settings_smtp_validation_succeeded',
  /** When the user clicks Not now or tries to leave the page before validating. */
  SMTP_VALIDATION_SKIP_REQUESTED = 'settings_smtp_validation_skip_requested',
  /** When user confirms leaving the page before finishing validation. */
  SMTP_VALIDATION_SKIP_CONFIRMED = 'settings_smtp_validation_skip_confirmed',
  /** When user clicks Cancel when the confirm dialog opens, meaning they don't leave the page. */
  SMTP_VALIDATION_SKIP_CANCELED = 'settings_smtp_validation_skip_canceled',
  /** When the user clicks Connect a Google/Office 365 email. */
  GOOGLE_OFFICE365_SETUP_FORCED = 'settings_google_office365_setup_forced',

  /** When the Gmail/Office 365 OAuth popup is closed. Does not necessarily mean OAuth succeeded or failed. */
  OAUTH_POPUP_CLOSED = 'settings_oauth_popup_closed',
  /** When we try to open the OAuth popup window but the browser blocks it. */
  OAUTH_POPUP_BLOCKED = 'settings_oauth_popup_blocked',
  /** When our wait for Gmail/Office 365 OAuth success times out and we show the help banner. */
  OAUTH_SUCCESS_TIMED_OUT = 'settings_oauth_success_timed_out',
  /** When we show the help banner despite Gmail/Office 365 OAuth completing successfully. */
  OAUTH_SUCCESS_TIMED_OUT_ERRONEOUSLY = 'settings_oauth_success_timed_out_erroneously',

  /** On the channel edit page, when the user confirms sending the validation email. */
  SMTP_VALIDATION_CONFIRMED = 'settings_smtp_validation_confirmed',
  /** On the channel edit page, when the user clicks cancel when the send validation email dialog pops up. */
  SMTP_VALIDATION_CANCELED = 'settings_smtp_validation_canceled',

  /** When the user is linked to the create inbox page from the client to force a Google OAuth flow. */
  GOOGLE_OAUTH_FLOW_TRIGGERED = 'settings_google_oauth_flow_triggered',
  /** When the user is linked to the create inbox page from the client to force an Office365 OAuth flow. */
  OFFICE365_OAUTH_FLOW_TRIGGERED = 'settings_office365_oauth_flow_triggered',

  /*
   * Channel creator events.
   */

  /** Events associated with completing a step. */
  CHANNEL_CREATOR_ACCOUNT_TYPE_STEP_COMPLETED = 'settings_channel_creator_account_type_step_completed',
  CHANNEL_CREATOR_EMAIL_INPUT_STEP_COMPLETED = 'settings_channel_creator_email_input_step_completed',
  CHANNEL_CREATOR_CONNECTION_STEP_COMPLETED = 'settings_channel_creator_connection_step_completed',
  CHANNEL_CREATOR_SETTINGS_STEP_COMPLETED = 'settings_channel_creator_settings_step_completed',
  CHANNEL_CREATOR_INBOX_NAME_STEP_COMPLETED = 'settings_channel_creator_inbox_name_step_completed',

  /** Events associated with DNS verification. */
  CHANNEL_CREATOR_DNS_FETCH_STARTED = 'settings_channel_creator_dns_fetch_started',
  CHANNEL_CREATOR_DNS_FETCH_SUCCEEDED = 'settings_channel_creator_dns_fetch_succeeded',
  CHANNEL_CREATOR_DNS_FETCH_FAILED = 'settings_channel_creator_dns_fetch_failed',
  CHANNEL_CREATOR_DNS_VERIFICATION_STARTED = 'settings_channel_creator_dns_verification_started',
  CHANNEL_CREATOR_DNS_VERIFICATION_SKIPPED = 'settings_channel_creator_dns_verification_skipped',
  CHANNEL_CREATOR_DNS_VERIFICATION_SUCCEEDED = 'settings_channel_creator_dns_verification_succeeded',
  CHANNEL_CREATOR_DNS_VERIFICATION_FAILED = 'settings_channel_creator_dns_verification_failed',

  /** Events associated with custom smtp setup in the channel creator.. */
  CHANNEL_CREATOR_CUSTOM_SMTP_STARTED = 'settings_channel_creator_custom_smtp_started',
  CHANNEL_CREATOR_CUSTOM_SMTP_SKIPPED = 'settings_channel_creator_custom_smtp_skipped',
  CHANNEL_CREATOR_CUSTOM_SMTP_SUCCEEDED = 'settings_channel_creator_custom_smtp_succeeded',
  CHANNEL_CREATOR_CUSTOM_SMTP_FAILED = 'settings_channel_creator_custom_smtp_failed',

  /*
   * Events tracked during channel creation.
   */

  // General channel creation events.
  /** When the user chooses the empty inbox option. */
  EMPTY_FOLDER_OPTION_CHOSEN = 'settings_empty_folder_option_chosen',
  /** When the user chooses a channel type to create (e.g. Google). */
  CHANNEL_TYPE_CHOSEN = 'settings_channel_type_chosen',
  /** When the user chooses a channel subtype (e.g. Google Group). */
  CHANNEL_SUBTYPE_CHOSEN = 'settings_channel_subtype_chosen',
  /** When the user has finished setting up all channel options and we are about to hit the backend to create the channel. */
  CHANNEL_READY_FOR_CREATION = 'settings_channel_ready_for_creation',
  /** When a channel is created at the end of the flow. */
  CHANNEL_CREATED = 'settings_channel_created',
  /** When an inbox is created at the end of the flow. */
  INBOX_CREATED = 'settings_inbox_created',
  /** When something goes wrong when creating the channel/inbox and we delete the channel we've already created. */
  CHANNEL_CREATION_ROLLBACK = 'settings_channel_creation_rollback',

  // Events specific to the email channel creation flow.
  /** When the user completes the email form. */
  EMAIL_FORM_COMPLETED = 'settings_email_form_completed',
  /** When we calculate what email provider the user's email uses: smtp | google | office365. */
  EMAIL_PROVIDER_CHOSEN = 'settings_email_provider_chosen',
  /** When the OAuth authentication succeeds. */
  OAUTH_ACCOUNT_SAVED = 'settings_oauth_account_saved',
  /** When the OAuth account authenticated already exists for the company. */
  OAUTH_ACCOUNT_ALREADY_EXISTS = 'settings_oauth_account_already_exists',
  /** When the email filled out earlier is different from the OAuth-ed account's email. */
  OAUTH_ACCOUNT_ADDRESS_MISMATCH = 'settings_oauth_account_address_mismatch',
  /** When the user overrides our alert to continue using the mismatched address for the channel. */
  OAUTH_ACCOUNT_ADDRESS_MISMATCH_OVERRIDDEN = 'settings_oauth_account_address_mismatch_overridden',
  /** When the user decides to not proceed with the mismatched channel and OAuth addresses. */
  OAUTH_ACCOUNT_ADDRESS_MISMATCH_CANCELED = 'settings_oauth_account_address_mismatch_canceled',
  /** When we recognize the user's address as an Office 365 address. */
  OFFICE365_PROVIDER_CHOSEN = 'settings_office365_provider_chosen',
  /** When we recognize the user's address as a Google address. */
  GOOGLE_PROVIDER_CHOSEN = 'settings_google_provider_chosen',
  /** When we recognize the user's address as non Google/Office 365. */
  SMTP_PROVIDER_CHOSEN = 'settings_smtp_provider_chosen',
  /** When the user forces the Office 365 flow by holding the shift key and pressing the Next button. */
  OFFICE365_PROVIDER_FORCED = 'settings_office365_provider_forced',
  /** When the user forces the Google flow by holding the alt key and pressing the Next button. */
  GOOGLE_PROVIDER_FORCED = 'settings_google_provider_forced',
  /** When the user chooses Gmail account. */
  GMAIL_ACCOUNT_TYPE_CHOSEN = 'settings_gmail_account_type_chosen',
  /** When the user chooses Google group account. */
  GOOGLE_GROUP_ACCOUNT_TYPE_CHOSEN = 'settings_google_group_account_type_chosen',
  /** When the user chooses Office 365 account. */
  OFFICE365_ACCOUNT_TYPE_CHOSEN = 'settings_office365_account_type_chosen',
  /** When the user chooses Distribution List. */
  DISTRIBUTION_LIST_ACCOUNT_TYPE_CHOSEN = 'settings_distribution_list_account_type_chosen',
  /** When the user pins the newly created inbox to other workspaces. */
  SETTINGS_PIN_INBOX_TO_TEAMMATE_WORKSPACES = 'settings_pin_inbox_to_teammate_workspaces',

  /** When the user clicks Add inbox on one of the inbox list pages. */
  ADD_INBOX_CLICKED = 'settings_add_inbox_clicked',

  /** When the user clicks Add a Channel in the side menu of an existing inbox's settings page. */
  ADD_CHANNEL_CLICKED = 'settings_add_channel_clicked',

  CONVERSATION_SHARED_DRAFT_VIEW = 'conversation.shared_draft.view',
  // Applications (P4 and App Store)

  APPS_P4_SEARCH = 'p4.manager.search',
  APPS_P4_ALL_PLUGINS_CLICK = 'p4.manager.click',
  APPS_P4_PIN = 'p4.manager.pin',
  APPS_P4_UNPIN = 'p4.manager.unpin',
  APPS_SETTINGS_ENABLE_WORKFLOW = 'integration-enable-workflow',
  APPS_SETTINGS_ENABLE_COMPLETED = 'integration-enable-completed',
  APPS_SETTINGS_UPDATE_COMPLETED = 'app-settings.integration_updated',
  APPS_SETTINGS_LEARN_MORE = 'app-settings.integration_learn_more',
  APPS_SETTINGS_DISABLE_COMPLETED = 'integration-disable-completed',
  APPS_SETTINGS_SEARCH = 'app-settings.search',
  APPS_SETTINGS_CLICK = 'app-settings.click',
  APPS_SETTINGS_CATEGORY_CLICK = 'app-settings.categories.click',
  APPS_SETTINGS_PAGE_VIEW = 'app-settings.page.view',
  APPS_SETTINGS_START_USING = 'integration-enable-completed.start_using',
  APPS_SETTINGS_BANNER_OPEN_PLUGIN = 'integration-enable-completed.popup_click',
}

export enum SurveyTypesEnum {
  CSAT_FIVE_STAR = 'csat_five_star',
  CSAT_THUMBS = 'csat_thumbs',
}

export enum ComposerVariableTypesEnum {
  DEFAULT = 'default',
  CUSTOM = 'custom',
}

export enum WorkspaceTagNestTypesEnum {
  NEST_UNDER = 'nest_under',
  UNNEST = 'unnest',
}

export enum WorkspaceDisplaySectionTrackingTypesEnum {
  INDIV_SUB_INBOX = 'indiv_sub_inbox',
  TAGS = 'tags',
  TEAM_INBOX = 'team_inbox',
  CUSTOM_VIEW = 'custom_view',
  DELEGATED_INBOX = 'delegated_inbox',
  TEAMMATES = 'teammates',
  LENS = 'lens',
  STARRED = 'starred',
  DRAFTS = 'drafts',
  SENT = 'sent',
  LABELS = 'labels',
  TEAM_VIEW = 'team_view',
  INBOX_VIEW = 'inbox_view',
  ALL_TEAMMATES_VIEW = 'all_teammates_view',
  P1_REC_BANNER = 'p1_recommendations_banner',
  P1_REC_HELP_TIP = 'p1_recommendations_help_tip',
}

export enum WorkspaceDisplayActionsEnum {
  HIDE = 'hide',
  DISPLAY = 'display',
  ADD = 'add',
  EDIT = 'edit',
  VIEW_SIDEBAR = 'view_sidebar',
  NEW_SUBTAG = 'new_subtag',
  QUICK_RULE = 'quick_rule',
  DISMISS = 'dismiss',
}

export enum WorkspaceDisplayItemsEnum {
  INFO_TOOLTIP = 'info_tooltip',
  BANNER = 'banner',
}

export enum WorkspaceDisplayObjectsEnum {
  AUTO_RECOMMENDATIONS = 'auto_recommendations',
}

export enum WorkspaceDisplayChevronSectionsEnum {
  INBOX = 'inbox',
  TEAM_INBOXES = 'team_inboxes',
  TEAM_SECTION = 'team_section',
  PERSONAL_SECTION = 'personal_section',
  LENSES = 'lenses',
  TEAMMATES = 'teammates',
  DELEGATED_SECTION = 'delegated_section',
  DELEGATED_INBOX = 'delegated_inbox',
}

export enum WorkspaceDisplayChevronActionsEnum {
  EXPAND = 'expand',
  COLLAPSE = 'collapse',
}

export enum PopoutLocationsEnum {
  MORE_MENU = 'More menu',
  DOUBLE_CLICK_P2 = 'Double click P2',
  COMMENT_UNFURL = 'Comment unfurl',
}

export enum ReplyLocationsEnum {
  AUTO = 'Auto',
  BOTTOM_OF_CONVERSATION = 'Bottom of conversation',
  KEYBOARD = 'Keyboard',
  MESSAGE_HEADER = 'Message header',
  PLUGIN_API = 'Plugin API',
  THREE_DOTS = 'Three dots',
  TOP_LEFT_BUTTON = 'Top left button',
  DRAFT_BANNER_PRIVATE_REPLY_TOOLTIP = 'Draft banner private reply tooltip',
  SHARED_DRAFT_BANNER_PRIVATE_REPLY_TOOLTIP = 'Shared Draft banner private reply tooltip',
}

export enum SnoozeLocationsEnum {
  ACTIVITY_LINE = 'Activity line',
  TOPBAR = 'Snooze dropdown',
  COMPOSER = 'Reminder dropdown',
  SEND_AND_SNOOZE = 'Send and snooze',
}

export enum ChangeCalendarFocusedDayMethodsEnum {
  TODAY_BUTTON = 'Today button',
  PREVIOUS_BUTTON = 'Previous button',
  NEXT_BUTTON = 'Next button',
  DAY_PICKER = 'Day picker',
}

export enum CalendarTypesEnum {
  LARGE = 'large',
  SIDE = 'side',
}

export enum CalendarModalTypesEnum {
  RESCHEDULE = 'reschedule',
  DELETE = 'delete',
}

export enum CalendarModalActionsEnum {
  CONFIRM = 'confirm',
  CANCEL = 'cancel',
}

export enum InviteTeammatePopoverAccessPointsEnum {
  WORKSPACE_TEAMMATES_DROPDOWN = 'workspace_teammates_dropdown',
  ASSIGN_DROPDOWN = 'assign_dropdown',
  COMMENT_COMPOSER_MENTION_AUTOCOMPLETE = 'comment_composer_mention_autocomplete',
  START_GUIDE = 'start_guide',
}

export enum OnboardingEventsEnum {
  STARTED = 'started',
  STARTED_PERSONNA = 'started_personna',
  COMPLETED_IDENTITY_INFO = 'completed_identity_info',
  COMPLETED_PERSONNA = 'completed_personna',
  COMPLETED_COMPANY_DATA = 'completed_company_data',
  COMPLETED = 'completed',
}

export enum ChannelMassArchiveActionsEnum {
  DIALOG_DISMISS = 'dialog_dismiss',
  DIALOG_TRIGGER = 'dialog_trigger',
}

/** Values for the `entry_point` property of interaction events. */
export enum EventEntryPointsEnum {
  MANUAL = 'manual',
  KEYBOARD_SHORTCUT = 'keyboard_shortcut',
  MARKDOWN = 'markdown',
}

// For actions taken on activities in the P3 timeline.
export enum TimelineActivityActionsEnum {
  CLICK_GROUP_EXPAND = 'click_group_expand',
  CLICK_RULE_ARCHIVE = 'click_rule_archive',
  CLICK_RULE_ASSIGNMENT = 'click_rule_assignment',
  CLICK_RULE_FORWARD = 'click_rule_forward',
  CLICK_RULE_MOVE = 'click_rule_move',
  CLICK_RULE_REOPEN = 'click_rule_reopen',
  CLICK_RULE_TAG = 'click_rule_tag',
  CLICK_RULE_UNTAG = 'click_rule_untag',
  CLICK_RULE_TOPIC_ADDED = 'click_rule_topic_added',
  CLICK_RULE_TOPIC_REMOVED = 'click_rule_topic_removed',
  CLICK_RULE_NOTIFY_TEAMMATE = 'click_rule_notify_teammate',
  CLICK_RULE_NOTIFY_YOU = 'click_rule_notify_you',
  CLICK_RULE_SNOOZE = 'click_rule_snooze',
  CLICK_SPLIT_CONVERSATION_LINK_TO_ORIGINAL = 'click_split_conversation_link_to_original',
  CLICK_SPLIT_CONVERSATION_LINK_TO_NEW = 'click_split_conversation_link_to_new',
}

// For actions taken on the P3 recent communications banner
export enum RecentCommunicationsBannerActionsEnum {
  CLOSE = 'close',
  VIEW = 'view',
}

export enum RecentCommunicationsBannerLocationsEnum {
  ABOVE_COMPOSER = 'above_composer',
  WITHIN_COMPOSER = 'within_composer',
}

// For different sections in the P4 contacts plugin
export enum ConversationListSectionNamesEnum {
  LAST_7D = 'last_7d',
  NONE = 'none',
  OLDER = 'older',
}

export enum SchedulingSectionsEnum {
  SCHEDULING_LINK = 'scheduling_link',
  MEETING_TIMES = 'meeting_times',
}

export enum SchedulingLinksAccessPointsEnum {
  P4,
  COMPOSER,
}

export enum NamespaceTypesEnum {
  PRIVATE = 'private',
  TEAM = 'team',
}

export enum ConversationComposerBannerTypesEnum {
  MENTION_INSTEAD = 'mention_instead',
  MASS_TEXT = 'mass_text',
  CONFLICTING_COPY = 'conflicting_copy',
  ATTACHMENTS_UPLOADING = 'attachments_uploading',
  RAW_VARIABLES = 'raw_variables',
}

export enum ConversationComposerWarningDialogTypesEnum {
  NO_SUBJECT = 'no_subject',
  NO_ATTACHMENTS = 'no_attachments',
  MANY_RECIPIENTS = 'many_recipients',
  FAILED_ATTACHMENTS = 'failed_attachments',
  UPLOADING_ATTACHMENTS = 'uploading_attachments',
  RAW_VARIABLES = 'raw_variables',
}

export enum SettingsTagTypesEnum {
  TEAM = 'team',
  INDIVIDUAL = 'individual',
}

export enum SettingsTrackingTypesEnum {
  COMPANY = 'company',
  TEAM = 'team',
  PERSONAL = 'personal',
}

export enum SingleTagCreationEntryPointsEnum {
  SETTINGS_MENU = 'settings_menu',
  THREE_DOT_SELECTION = 'three_dot_selection',
}

export enum ConversationComposerTooltipLocationsEnum {
  DRAFT_BANNER = 'draft_banner',
}

export enum ConversationComposerTooltipActionsEnum {
  SEND_PRIVATE_REPLY = 'send_private_reply',
}

export enum ConversationComposerBannerActionsEnum {
  SHARE_DRAFT = 'share_draft',
  CLOSE = 'close',
}

export enum BulkActionsTrackingTypesEnum {
  ARCHIVE = 'archive',
  SNOOZE = 'snooze',
  UNSNOOZE = 'unsnooze',
  REOPEN = 'reopen',
  TRASH = 'trash',
  DESTROY = 'destroy',
  RESTORE = 'restore',
  MOVE = 'move',
  MERGE = 'merge',
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
  TAG = 'tag',
  UNTAG = 'untag',
  STAR = 'star',
  UNSTAR = 'unstar',
  MARK_READ = 'mark_read',
  MARK_UNREAD = 'mark_unread',
  MARK_SPAM = 'mark_spam',
  REPORT_PHISHING = 'report_phishing',
}

export enum CellsActionsEnum {
  ARCHIVE = 'archive',
  TRASH = 'trash',
  REOPEN = 'reopen',
  RESTORE = 'restore',
  MARK_READ = 'mark_read',
  MARK_UNREAD = 'mark_unread',
}

interface TrackingEvent {
  time: number;
  type: TrackingTypesEnum;
  name?: string;
  value?: unknown;
}

interface TrackingEventBatch {
  time: number;
  events: ReadonlyArray<TrackingEvent>;
}

/*
 * Constants.
 */

const trackingInterval = isDevelopment() ? 5 * 1000 : 30 * 1000; // ms

/*
 * API.
 */

let dataBatchEventEndpoint: string | undefined;

export function setupTracking(store: Store<AppState>) {
  const shouldTrack = isProduction() || isNetlify();
  if (!shouldTrack) {
    return;
  }

  dataBatchEventEndpoint = `${store.getState().data.company.url}/data_event_batch`;
}

export async function trackEvent(type: TrackingTypesEnum, name?: string, value?: unknown) {
  const event = {
    time: performance.now(),
    type,
    name,
    value,
  };

  if (globalWindow.shouldLogInteractionTracking) {
    console.log('Tracking event', event);
  } // eslint-disable-line no-console

  if (!dataBatchEventEndpoint) {
    return;
  }

  enqueueTrackingEvent(event);
  startReportingTrackingEvents();
}

export async function trackInterfaceEvent(eventName: InterfaceEventsEnum, eventData?: unknown) {
  return trackEvent(TrackingTypesEnum.INTERFACE_EVENT, eventName, maybeAddWindowContext(eventData));
}

function maybeAddWindowContext(eventData?: unknown) {
  // React Native doesn't have window data.
  if (!globalWindow.location) {
    return eventData;
  }

  const windowContext = findLocationContext(globalWindow.location.pathname);
  if (!windowContext || !isObject(eventData)) {
    return eventData;
  }

  return {...eventData, window_context: windowContext};
}

/*
 * Helpers.
 */

const locationContexts = [
  {
    type: 'teammate',
    regexp: new RegExp(`^/inboxes/teammates/`),
  },
  {
    type: 'team',
    regexp: new RegExp(`^/inboxes/teams/`),
  },
];

function findLocationContext(url: string) {
  const context = locationContexts.find((c) => c.regexp.test(url));
  return (context && context.type) || null;
}

/*
 * Batching.
 */

const trackingEventQueue: TrackingEvent[] = [];

function enqueueTrackingEvent(event: TrackingEvent) {
  trackingEventQueue.push(event);
}

/** How many events to send per batch. */
const dequeueEventCount = 100;

function dequeueTrackingEventBatch(): TrackingEventBatch | undefined {
  if (trackingEventQueue.length === 0) {
    return undefined;
  }

  return {
    time: performance.now(),
    events: trackingEventQueue.splice(0, dequeueEventCount),
  };
}

/* If we have more than this number of events, throw away events that fail to be posted. */
const requeueMaxEventCount = 500;

function requeueTrackingEventBatch(batch: TrackingEventBatch) {
  if (trackingEventQueue.length > requeueMaxEventCount) {
    reportToBugsnag(new Error(`[tracking] Buffer full, discarding ${batch.events.length} events`));
    return;
  }

  trackingEventQueue.splice(0, 0, ...batch.events);
}

/*
 * Reporting.
 */

let isReportingTrackingEvents = false;

function startReportingTrackingEvents() {
  if (isReportingTrackingEvents) {
    return;
  }

  let handle: number = 0;

  globalWindow.setInstrumentedInterval(() => {
    // Cancel the previous idle callback, in case it hasn't happened yet.
    globalWindow.cancelIdleCallback(handle);
    // Wait for the browser to be idle.
    handle = globalWindow.requestInstrumentedIdleCallback(reportTrackingEventsAsync, {
      source: TaskSourcesEnum.LOGGING,
    });
  }, trackingInterval);

  globalWindow.addEventListener('unload', (event) => {
    if (!dataBatchEventEndpoint) {
      return;
    }

    for (let batch = dequeueTrackingEventBatch(); batch; batch = dequeueTrackingEventBatch()) {
      const headers = {type: 'application/json'};
      const blob = new Blob([JSON.stringify(createTrackingEventPostBody(batch))], headers);
      navigator.sendBeacon(dataBatchEventEndpoint, blob);
    }
  });

  isReportingTrackingEvents = true;
}

function createTrackingEventPostBody(batch: TrackingEventBatch) {
  return {
    events: batch.events.map((event) => ({
      type: event.type,
      name: event.name,
      value: !isUndefined(event.value) ? JSON.stringify(event.value) : undefined,
      time_offset: Math.round(event.time - batch.time),
    })),
  };
}

async function reportTrackingEventsAsync() {
  const batch = dequeueTrackingEventBatch();
  if (!batch) {
    return;
  }

  if (globalWindow.shouldLogInteractionTracking) {
    // eslint-disable-next-line no-console
    console.log('Tracking report', batch);
  }

  try {
    if (!dataBatchEventEndpoint) {
      return;
    }

    await postAsync(dataBatchEventEndpoint, createTrackingEventPostBody(batch));
  } catch (error) {
    requeueTrackingEventBatch(batch);
    throw error;
  }
}

export function getVisualSettings(url: SettingsUrlModel, user: TeammateModel) {
  if (hasProperty(url, 'namespace')) {
    if (isTeammateNamespace(String(url.namespace))) {
      if (url.namespace !== user.namespace) {
        return 'teammate';
      }

      if (url.namespace === 'global') {
        return 'global';
      }

      return 'individual';
    }
  }

  return 'shared';
}
